import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { calculateDerivedKPIs, operatingDaysFactor } from './calculateDerivedKPIs';
import { exchangeRates } from 'src/utils/const';
import { ReportTypes } from '../propTypes';

interface ExportParams {
  reportData: any;
  kpiData: any;
  inputEditState: Record<string, any>;
  currency: string;
  userName: string;
  userEmail: string;
  /** Selected report framing; workbook content may vary by path in a future iteration. */
  reportPath?: ReportTypes;
}

// --- Airbtics palette ---
const TEAL_DARK = '037A90';
const TEAL_LIGHT = 'D8EFEE';
const WHITE = 'FFFFFF';
const TEXT_DARK = '1F2937';
const TEXT_MED = '4B5563';
const BORDER_CLR = 'E5E7EB';

// --- Reusable style presets ---
const sectionHeader: XLSX.CellStyle = {
  font: { bold: true, color: { rgb: WHITE }, sz: 12, name: 'Calibri' },
  fill: { fgColor: { rgb: TEAL_DARK }, patternType: 'solid' },
  alignment: { horizontal: 'left', vertical: 'center' },
};

const subHeader: XLSX.CellStyle = {
  font: { bold: true, color: { rgb: TEAL_DARK }, sz: 11, name: 'Calibri' },
  fill: { fgColor: { rgb: TEAL_LIGHT }, patternType: 'solid' },
  alignment: { horizontal: 'left', vertical: 'center' },
};

const labelStyle: XLSX.CellStyle = {
  font: { bold: false, color: { rgb: TEXT_DARK }, sz: 10, name: 'Calibri' },
  alignment: { horizontal: 'left' },
};

const labelBold: XLSX.CellStyle = {
  font: { bold: true, color: { rgb: TEXT_DARK }, sz: 10, name: 'Calibri' },
  alignment: { horizontal: 'left' },
};

const valRight: XLSX.CellStyle = {
  font: { color: { rgb: TEXT_DARK }, sz: 10, name: 'Calibri' },
  alignment: { horizontal: 'right' },
};

const valRightBold: XLSX.CellStyle = {
  font: { bold: true, color: { rgb: TEXT_DARK }, sz: 10, name: 'Calibri' },
  alignment: { horizontal: 'right' },
};

const thinBorder = { style: 'thin', color: { rgb: BORDER_CLR } } as const;
const borderedVal: XLSX.CellStyle = {
  ...valRight,
  border: { bottom: thinBorder },
};

const compHeader: XLSX.CellStyle = {
  font: { bold: true, color: { rgb: WHITE }, sz: 10, name: 'Calibri' },
  fill: { fgColor: { rgb: TEAL_DARK }, patternType: 'solid' },
  alignment: { horizontal: 'left', vertical: 'center' },
};

const compHeaderRight: XLSX.CellStyle = { ...compHeader, alignment: { horizontal: 'right', vertical: 'center' } };

const projHeader: XLSX.CellStyle = {
  font: { bold: true, color: { rgb: TEAL_DARK }, sz: 10, name: 'Calibri' },
  fill: { fgColor: { rgb: TEAL_LIGHT }, patternType: 'solid' },
  alignment: { horizontal: 'right', vertical: 'center' },
};

const projLabel: XLSX.CellStyle = {
  font: { color: { rgb: TEXT_MED }, sz: 10, name: 'Calibri' },
  alignment: { horizontal: 'left' },
  border: { bottom: thinBorder },
};

const projVal: XLSX.CellStyle = {
  font: { color: { rgb: TEXT_DARK }, sz: 10, name: 'Calibri' },
  alignment: { horizontal: 'right' },
  border: { bottom: thinBorder },
};

const projValBold: XLSX.CellStyle = {
  font: { bold: true, color: { rgb: TEAL_DARK }, sz: 10, name: 'Calibri' },
  alignment: { horizontal: 'right' },
  border: { bottom: thinBorder },
};

// --- Helpers ---
function cur(value: number | null | undefined): number {
  if (value == null) return 0;
  return Math.round(value);
}

function pct(value: number | null | undefined): string {
  if (value == null) return '0.0%';
  return `${(Math.round(value * 10) / 10).toFixed(1)}%`;
}

function fmtCur(sym: string, v: number): string {
  return `${sym}${v.toLocaleString()}`;
}

function cell(v: any, s?: XLSX.CellStyle): XLSX.CellObject {
  const type = typeof v === 'number' ? 'n' : 's';
  return { v, t: type, s } as XLSX.CellObject;
}

function setCell(ws: XLSX.WorkSheet, r: number, c: number, v: any, s?: XLSX.CellStyle) {
  const ref = XLSX.utils.encode_cell({ r, c });
  ws[ref] = cell(v, s);
}

function setMerge(ws: XLSX.WorkSheet, sr: number, sc: number, er: number, ec: number) {
  if (!ws['!merges']) ws['!merges'] = [];
  ws['!merges'].push({ s: { r: sr, c: sc }, e: { r: er, c: ec } });
}

const LAST_COL_STANDARD = 7;
const LAST_COL_RENT_24MO = 24;

const REPORT_PATH_META: Record<
  ReportTypes,
  { docTitleSuffix: string; sheetName: string; fileSuffix: string }
> = {
  buy: {
    docTitleSuffix: 'Investment analysis',
    sheetName: 'Investment',
    fileSuffix: 'investment_analysis',
  },
  rent: {
    docTitleSuffix: 'Rental arbitrage analysis',
    sheetName: 'Rental arbitrage',
    fileSuffix: 'rental_arbitrage',
  },
  manage: {
    docTitleSuffix: 'Property management analysis',
    sheetName: 'Property mgmt',
    fileSuffix: 'property_management',
  },
};

/** Breakeven-style M1–M24 grid (matches CalculatorGraphSection BreakevenDataTable). */
function append24MonthBreakevenSection(
  ws: XLSX.WorkSheet,
  startRow: number,
  sym: string,
  startupCosts: number,
  monthlyProfitVal: number
): number {
  let r = startRow;
  const lc = LAST_COL_RENT_24MO;
  const hdr: XLSX.CellStyle = {
    ...projHeader,
    alignment: { horizontal: 'left', vertical: 'center' },
  };
  for (let c = 0; c <= lc; c++) {
    setCell(ws, r, c, c === 0 ? 'MONTHLY BREAKDOWN (24 MONTHS)' : '', sectionHeader);
  }
  setMerge(ws, r, 0, r, lc);
  r++;

  setCell(ws, r, 0, '', hdr);
  for (let m = 1; m <= 24; m++) {
    setCell(ws, r, m, `M${m}`, projHeader);
  }
  r++;

  const mStyle: XLSX.CellStyle = { ...valRight, border: { bottom: thinBorder } };
  setCell(ws, r, 0, 'Initial investment', labelStyle);
  for (let m = 1; m <= 24; m++) {
    setCell(ws, r, m, m === 1 ? fmtCur(sym, startupCosts) : '–', mStyle);
  }
  r++;

  setCell(ws, r, 0, 'Net profit', labelStyle);
  for (let m = 1; m <= 24; m++) {
    setCell(ws, r, m, fmtCur(sym, monthlyProfitVal), mStyle);
  }
  r++;

  setCell(ws, r, 0, 'Cumulative profit', labelStyle);
  for (let m = 1; m <= 24; m++) {
    setCell(ws, r, m, fmtCur(sym, monthlyProfitVal * m), mStyle);
  }
  r++;

  setCell(ws, r, 0, 'Remaining to breakeven', labelStyle);
  for (let m = 1; m <= 24; m++) {
    const cumulative = monthlyProfitVal * m;
    const remaining = startupCosts - cumulative;
    const recovered = remaining <= 0;
    setCell(ws, r, m, recovered ? 'Recovered' : fmtCur(sym, remaining), mStyle);
  }
  r++;
  r++;
  return r;
}

// ============================
// MAIN EXPORT FUNCTION
// ============================
export function exportReportToExcel({
  reportData,
  kpiData,
  inputEditState,
  currency,
  userName,
  userEmail,
  reportPath = 'buy',
}: ExportParams) {
  const pi = reportData.primary_input;
  const fi = reportData.fincal_input || {};
  const sym = exchangeRates[currency]?.symbol || '$';
  const baseRevenue = kpiData?.ltm_revenue || 0;
  const derived = calculateDerivedKPIs(inputEditState, baseRevenue);
  const proratedAnnualRevenue = Math.round(baseRevenue * operatingDaysFactor(inputEditState.owner_occupied_days));

  const annualRevenue = cur(proratedAnnualRevenue);
  const monthlyRevenue = Math.round(annualRevenue / 12);
  const adr = cur(kpiData?.ltm_nightly_rate);
  const occupancy = kpiData?.ltm_occupancy_rate || 0;

  const opex = cur(derived.annual_operating_expenses_total);
  const monthlyOpex = Math.round(opex / 12);
  const mortgage = cur(derived.mortgage_annual);
  const monthlyMortgage = Math.round(mortgage / 12);
  const taxes = cur(derived.taxes_total);
  const monthlyTaxes = Math.round(taxes / 12);
  const monthlyFinancing = monthlyMortgage + monthlyTaxes;

  const netProfit = cur(derived.yearly_net_profit_before_tax);
  const monthlyProfit = Math.round(netProfit / 12);
  const totalExpenses = cur(derived.total_expenses);
  const totalCashInvested = cur(derived.total_cash_invested);
  const cocReturn = derived.cash_on_cash_return;
  const capRate = derived.cap_rate;

  const propertyPrice = cur(inputEditState.property_price || fi.property_price || 0);
  const monthlyRent = cur(inputEditState.monthly_rent || fi.monthly_rent || 0);
  const downPayment = cur(inputEditState.down_payment || fi.loan?.down_payment || 0);
  const interestRate = inputEditState.interest_rate || fi.loan?.interest_rate || 0;
  const loanTenure = inputEditState.loan_tenure || fi.loan?.loan_tenure || 30;
  const isLoanTaken = inputEditState.is_loan_taken || fi.loan?.is_loan_taken || false;

  const closingCost = cur(inputEditState.closing_cost || fi.startup_costs?.closing_cost || 0);
  const furnishing = cur(inputEditState.furnishing_cost || fi.startup_costs?.furnishing_cost || 0);
  const remodelling = cur(inputEditState.remodelling || fi.startup_costs?.remodelling || 0);

  const managementFeePct = Math.min(inputEditState.management_fee || 0, 40);
  const managementFeeDollarAnnual = Math.round((managementFeePct / 100) * annualRevenue);
  const managementFeeDollarMonthly = Math.round(managementFeeDollarAnnual / 12);

  const opexMonthly: [string, number][] = [
    ['HOA', Math.round(cur(inputEditState.hoa_fee_annual || 0) / 12)],
    ['Maintenance', Math.round(cur(inputEditState.maintenance_annual || 0) / 12)],
    ['Insurance', Math.round(cur(inputEditState.insurance_annual || 0) / 12)],
    ['Electricity', Math.round(cur(inputEditState.electricity_annual || 0) / 12)],
    ['Wifi', Math.round(cur(inputEditState.wifi_annual || 0) / 12)],
    ['Gas', Math.round(cur(inputEditState.gas_annual || 0) / 12)],
    ['Lodging Tax', Math.round(cur(inputEditState.lodging_tax || 0) / 12)],
    ['Marketing', Math.round(cur(inputEditState.marketing || 0) / 12)],
    ['Tech', Math.round(cur(inputEditState.tech || 0) / 12)],
    ['Vacancy Cost', Math.round(cur(inputEditState.vacancy_cost || 0) / 12)],
    ['Other', Math.round(cur(inputEditState.others_annual || 0) / 12)],
  ];

  const isBuyPath = reportPath === 'buy';
  const isRentPath = reportPath === 'rent';
  const isManagePath = reportPath === 'manage';
  const pathMeta = REPORT_PATH_META[reportPath];
  const maxDataCol = isRentPath ? LAST_COL_RENT_24MO : LAST_COL_STANDARD;

  const addressShort = pi.address_short || pi.address_full?.split(',')[0] || 'Property';
  const addressFull = pi.address_full || addressShort;
  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const noi = annualRevenue - opex;
  const monthlyNoi = Math.round(noi / 12);
  const ownerPayoutAnnual = cur(derived.owner_payout);
  const ownerPayoutMonthly = Math.round(ownerPayoutAnnual / 12);
  const startupCostsTotal = cur(derived.startup_costs_total);
  const monthlyProfitBreakeven = derived.monthly_profit;

  // Build worksheet
  const ws: XLSX.WorkSheet = {};
  let row = 0;

  // ====== SECTION 1: HEADER ======
  const titleStyle: XLSX.CellStyle = {
    font: { bold: true, color: { rgb: TEAL_DARK }, sz: 14, name: 'Calibri' },
    alignment: { horizontal: 'left' },
  };
  const metaStyle: XLSX.CellStyle = {
    font: { color: { rgb: TEXT_MED }, sz: 10, name: 'Calibri' },
    alignment: { horizontal: 'left' },
  };

  setCell(ws, row, 0, `${addressFull} — ${pathMeta.docTitleSuffix}`, titleStyle);
  setMerge(ws, row, 0, row, LAST_COL_STANDARD);
  row++;
  setCell(ws, row, 0, `Generated ${dateStr}`, metaStyle);
  row++;
  setCell(ws, row, 0, `Prepared by: ${userName || 'Airbtics'}`, metaStyle);
  row++;
  setCell(ws, row, 0, userEmail || '', metaStyle);
  row++;
  row++; // blank

  // ====== SECTION 2: PROPERTY OVERVIEW ======
  for (let c = 0; c <= LAST_COL_STANDARD; c++) {
    setCell(ws, row, c, c === 0 ? 'PROPERTY OVERVIEW' : '', sectionHeader);
  }
  setMerge(ws, row, 0, row, LAST_COL_STANDARD);
  row++;

  if (isBuyPath) {
    setCell(ws, row, 0, 'Purchase Price', labelBold);
    setCell(ws, row, 1, fmtCur(sym, propertyPrice), valRightBold);
    row++;
  } else if (isRentPath) {
    setCell(ws, row, 0, 'Monthly Rent', labelBold);
    setCell(ws, row, 1, fmtCur(sym, monthlyRent), valRightBold);
    row++;
  } else if (isManagePath) {
    setCell(ws, row, 0, 'Management Fee', labelBold);
    setCell(ws, row, 1, `${managementFeePct}%`, valRightBold);
    row++;
  }

  setCell(ws, row, 0, 'Bedrooms', labelStyle);
  setCell(ws, row, 1, pi.bedrooms || 0, valRight);
  setCell(ws, row, 2, 'Bathrooms', labelStyle);
  setCell(ws, row, 3, pi.bathrooms || 0, valRight);
  row++;

  setCell(ws, row, 0, 'Accommodates', labelStyle);
  setCell(ws, row, 1, pi.accommodates || 0, valRight);
  row++;

  setCell(ws, row, 0, 'Location', labelStyle);
  setCell(ws, row, 1, addressShort, labelStyle);
  row++;

  if (pi.year_built) {
    setCell(ws, row, 0, 'Year Built', labelStyle);
    setCell(ws, row, 1, pi.year_built, valRight);
    if (pi.property_size_sqm) {
      setCell(ws, row, 2, 'Property Size (m²)', labelStyle);
      setCell(ws, row, 3, pi.property_size_sqm, valRight);
    }
    row++;
  }
  row++; // blank

  // ====== SECTION 3: KEY METRICS ======
  for (let c = 0; c <= LAST_COL_STANDARD; c++) {
    setCell(ws, row, c, c === 0 ? 'KEY METRICS' : '', sectionHeader);
  }
  setMerge(ws, row, 0, row, LAST_COL_STANDARD);
  row++;

  if (isBuyPath) {
    setCell(ws, row, 0, 'Monthly Cash Flow', labelStyle);
    setCell(ws, row, 1, fmtCur(sym, monthlyProfit), valRightBold);
    setCell(ws, row, 3, 'Annual Cash Flow', labelStyle);
    setCell(ws, row, 4, fmtCur(sym, netProfit), valRightBold);
    setCell(ws, row, 6, 'CoC Return', labelStyle);
    setCell(ws, row, 7, cocReturn != null ? pct(cocReturn) : 'N/A', valRightBold);
    row++;

    setCell(ws, row, 0, 'Annual Revenue', labelStyle);
    setCell(ws, row, 1, fmtCur(sym, annualRevenue), valRight);
    setCell(ws, row, 3, 'Annual Expenses', labelStyle);
    setCell(ws, row, 4, fmtCur(sym, totalExpenses), valRight);
    setCell(ws, row, 6, 'Cash Invested', labelStyle);
    setCell(ws, row, 7, fmtCur(sym, totalCashInvested), valRight);
    row++;

    setCell(ws, row, 0, 'Cap Rate', labelStyle);
    setCell(ws, row, 1, capRate != null ? pct(capRate) : 'N/A', valRight);
    setCell(ws, row, 3, 'ADR', labelStyle);
    setCell(ws, row, 4, fmtCur(sym, adr), valRight);
    setCell(ws, row, 6, 'Occupancy', labelStyle);
    setCell(ws, row, 7, pct(occupancy), valRight);
    row++;

    setCell(ws, row, 0, 'Purchase Price', labelStyle);
    setCell(ws, row, 1, fmtCur(sym, propertyPrice), valRight);
    row++;
  } else {
    setCell(ws, row, 0, 'Monthly Cash Flow', labelStyle);
    setCell(ws, row, 1, fmtCur(sym, monthlyProfit), valRightBold);
    setCell(ws, row, 3, 'Annual Cash Flow', labelStyle);
    setCell(ws, row, 4, fmtCur(sym, netProfit), valRightBold);
    setCell(ws, row, 6, 'ADR', labelStyle);
    setCell(ws, row, 7, fmtCur(sym, adr), valRight);
    row++;

    setCell(ws, row, 0, 'Annual Revenue', labelStyle);
    setCell(ws, row, 1, fmtCur(sym, annualRevenue), valRight);
    setCell(ws, row, 3, 'Annual Expenses', labelStyle);
    setCell(ws, row, 4, fmtCur(sym, totalExpenses), valRight);
    if (isManagePath) {
      setCell(ws, row, 6, "Owner's Payout (Monthly)", labelStyle);
      setCell(ws, row, 7, fmtCur(sym, ownerPayoutMonthly), valRightBold);
    } else {
      setCell(ws, row, 6, 'Occupancy', labelStyle);
      setCell(ws, row, 7, pct(occupancy), valRight);
    }
    row++;

    if (isManagePath) {
      setCell(ws, row, 0, 'ADR', labelStyle);
      setCell(ws, row, 1, fmtCur(sym, adr), valRight);
      setCell(ws, row, 3, 'Occupancy', labelStyle);
      setCell(ws, row, 4, pct(occupancy), valRight);
      setCell(ws, row, 6, "Owner's Payout (Annual)", labelStyle);
      setCell(ws, row, 7, fmtCur(sym, ownerPayoutAnnual), valRightBold);
      row++;
    }
  }
  row++; // blank

  // ====== SECTION 4: REVENUE & EXPENSES ======
  for (let c = 0; c <= LAST_COL_STANDARD; c++) {
    setCell(ws, row, c, c === 0 ? 'REVENUE & EXPENSES' : '', sectionHeader);
  }
  setMerge(ws, row, 0, row, LAST_COL_STANDARD);
  row++;

  // Sub-headers
  setCell(ws, row, 0, '', subHeader);
  setCell(ws, row, 1, 'Revenue', subHeader);
  setCell(ws, row, 2, '', subHeader);
  setCell(ws, row, 3, '', subHeader);
  setCell(ws, row, 5, 'Operating Expenses (Monthly)', subHeader);
  setCell(ws, row, 6, '', subHeader);
  setCell(ws, row, 7, '', subHeader);
  setMerge(ws, row, 0, row, 3);
  setMerge(ws, row, 5, row, 7);
  row++;

  // Revenue rows (left) + Opex rows (right)
  const revRows: [string, string][] = [
    ['Average Daily Rate', fmtCur(sym, adr)],
    ['Occupancy Rate', pct(occupancy)],
    ['Monthly Revenue', fmtCur(sym, monthlyRevenue)],
    ['Annual Revenue', fmtCur(sym, annualRevenue)],
  ];

  const opexRows: [string, string][] = opexMonthly.map(([name, val]) => [name, fmtCur(sym, val)]);
  opexRows.push([`Mgmt Fee (${managementFeePct}%)`, fmtCur(sym, managementFeeDollarMonthly)]);

  const maxLeft1 = revRows.length;
  const maxRight = opexRows.length;

  for (let i = 0; i < Math.max(maxLeft1, maxRight); i++) {
    if (i < maxLeft1) {
      setCell(ws, row, 1, revRows[i][0], labelStyle);
      setCell(ws, row, 3, revRows[i][1], borderedVal);
    }
    if (i < maxRight) {
      setCell(ws, row, 5, opexRows[i][0], labelStyle);
      setCell(ws, row, 7, opexRows[i][1], borderedVal);
    }
    row++;
  }

  // Blank row before Cash Investment / totals
  row++;

  // Cash Investment + Financing (left), Monthly/Annual Total (right)
  if (isBuyPath) {
    setCell(ws, row, 0, '', subHeader);
    setCell(ws, row, 1, 'Cash Investment', subHeader);
    setCell(ws, row, 2, '', subHeader);
    setCell(ws, row, 3, '', subHeader);
    setCell(ws, row, 5, 'Monthly Total', labelBold);
    setCell(ws, row, 7, fmtCur(sym, monthlyOpex), valRightBold);
    setMerge(ws, row, 0, row, 3);
    row++;

    setCell(ws, row, 1, 'Down Payment', labelStyle);
    setCell(ws, row, 3, fmtCur(sym, downPayment), borderedVal);
    setCell(ws, row, 5, 'Annual Total', labelBold);
    setCell(ws, row, 7, fmtCur(sym, opex), valRightBold);
    row++;

    setCell(ws, row, 1, 'Closing Costs', labelStyle);
    setCell(ws, row, 3, fmtCur(sym, closingCost), borderedVal);
    row++;

    setCell(ws, row, 1, 'Renos & Furnishing', labelStyle);
    setCell(ws, row, 3, fmtCur(sym, remodelling + furnishing), borderedVal);
    row++;

    setCell(ws, row, 1, 'Total Cash Investment', labelBold);
    setCell(ws, row, 3, fmtCur(sym, totalCashInvested), valRightBold);
    row++;
    row++;

    if (isLoanTaken) {
      setCell(ws, row, 0, '', subHeader);
      setCell(ws, row, 1, 'Financing', subHeader);
      setCell(ws, row, 2, '', subHeader);
      setCell(ws, row, 3, '', subHeader);
      setMerge(ws, row, 0, row, 3);
      row++;

      setCell(ws, row, 1, 'Interest Rate', labelStyle);
      setCell(ws, row, 3, `${interestRate}%`, borderedVal);
      row++;

      setCell(ws, row, 1, 'Monthly Loan Payment', labelStyle);
      setCell(ws, row, 3, fmtCur(sym, monthlyMortgage), borderedVal);
      row++;

      setCell(ws, row, 1, 'Monthly Prop Tax + Ins', labelStyle);
      setCell(ws, row, 3, fmtCur(sym, monthlyTaxes), borderedVal);
      row++;

      setCell(ws, row, 1, 'Total Monthly Financing', labelBold);
      setCell(ws, row, 3, fmtCur(sym, monthlyFinancing), valRightBold);
      row++;
    }
  } else {
    setCell(ws, row, 5, 'Monthly Total', labelBold);
    setCell(ws, row, 7, fmtCur(sym, monthlyOpex), valRightBold);
    row++;
    setCell(ws, row, 5, 'Annual Total', labelBold);
    setCell(ws, row, 7, fmtCur(sym, opex), valRightBold);
    row++;
  }
  row++; // blank

  // ====== SECTION 5: FINANCIAL PROJECTIONS ======
  for (let c = 0; c <= LAST_COL_STANDARD; c++) {
    setCell(ws, row, c, c === 0 ? 'FINANCIAL PROJECTIONS' : '', sectionHeader);
  }
  setMerge(ws, row, 0, row, LAST_COL_STANDARD);
  row++;

  // Column headers
  const projLabelHeader: XLSX.CellStyle = {
    ...projHeader,
    alignment: { horizontal: 'left', vertical: 'center' },
  };
  setCell(ws, row, 0, '', projLabelHeader);
  setCell(ws, row, 1, '', projLabelHeader);
  setCell(ws, row, 2, 'Monthly', projHeader);
  setCell(ws, row, 3, 'Year 1', projHeader);
  setCell(ws, row, 4, 'Year 5', projHeader);
  setCell(ws, row, 5, 'Year 10', projHeader);
  setMerge(ws, row, 0, row, 1);
  row++;

  // Sub-header
  setCell(ws, row, 0, 'Financial Summary', labelBold);
  row++;

  const financingAnnual = mortgage + taxes;
  const projections: [string, number, number, boolean][] = [
    ['Revenue', monthlyRevenue, annualRevenue, false],
    ['Operating Expenses', monthlyOpex, opex, false],
    ['Operating Income', monthlyNoi, noi, false],
    ['Mortgage & Taxes', monthlyFinancing, financingAnnual, false],
    ['Cash Flow / Profit', monthlyProfit, netProfit, true],
  ];

  const skipMortgageTaxesRow =
    isManagePath && monthlyFinancing === 0 && financingAnnual === 0;

  projections
    .filter(([label]) => !(skipMortgageTaxesRow && label === 'Mortgage & Taxes'))
    .forEach(([label, monthly, annual, bold]) => {
      const ls = bold
        ? { ...projLabel, font: { ...projLabel.font, bold: true, color: { rgb: TEAL_DARK } } }
        : projLabel;
      const vs = bold ? projValBold : projVal;
      setCell(ws, row, 0, label, ls);
      setMerge(ws, row, 0, row, 1);
      setCell(ws, row, 2, fmtCur(sym, monthly), vs);
      setCell(ws, row, 3, fmtCur(sym, annual), vs);
      setCell(ws, row, 4, fmtCur(sym, annual * 5), vs);
      setCell(ws, row, 5, fmtCur(sym, annual * 10), vs);
      row++;
    });
  row++; // blank

  if (isRentPath) {
    row = append24MonthBreakevenSection(ws, row, sym, startupCostsTotal, monthlyProfitBreakeven);
  }

  // ====== SECTION 6: COMPARABLE PROPERTIES ======
  const selectedComps = (reportData.comps || []).filter((c: any) => c.is_selected === 1);
  if (selectedComps.length > 0) {
    for (let c = 0; c <= LAST_COL_STANDARD; c++) {
      setCell(ws, row, c, c === 0 ? 'COMPARABLE PROPERTIES' : '', sectionHeader);
    }
    setMerge(ws, row, 0, row, LAST_COL_STANDARD);
    row++;

    const compHeaders = ['#', 'Property Name', 'Bedrooms', 'Bathrooms', 'ADR', 'Occ%', 'Annual Rev', 'Link'];
    compHeaders.forEach((h, i) => {
      setCell(ws, row, i, h, i >= 4 ? compHeaderRight : compHeader);
    });
    row++;

    const compRowEven: XLSX.CellStyle = {
      font: { color: { rgb: TEXT_DARK }, sz: 9, name: 'Calibri' },
      border: { bottom: thinBorder },
    };
    const compRowOdd: XLSX.CellStyle = {
      ...compRowEven,
      fill: { fgColor: { rgb: 'F9FAFB' }, patternType: 'solid' },
    };

    selectedComps.forEach((comp: any, idx: number) => {
      const s = idx % 2 === 0 ? compRowEven : compRowOdd;
      const sr = { ...s, alignment: { horizontal: 'right' as const } };
      setCell(ws, row, 0, idx + 1, s);
      setCell(ws, row, 1, comp.name || 'Unnamed', s);
      setCell(ws, row, 2, comp.bedrooms ?? '', s);
      setCell(ws, row, 3, comp.bathrooms ?? '', s);
      setCell(ws, row, 4, comp.avg_booked_daily_rate_ltm ? fmtCur(sym, cur(comp.avg_booked_daily_rate_ltm)) : '--', sr);
      setCell(ws, row, 5, comp.avg_occupancy_rate_ltm ? pct(comp.avg_occupancy_rate_ltm) : '--', sr);
      setCell(ws, row, 6, comp.annualRevenueLTM ? fmtCur(sym, cur(comp.annualRevenueLTM)) : '--', sr);
      setCell(ws, row, 7, comp.listingID ? `https://www.airbnb.com/rooms/${comp.listingID}` : '', {
        ...s,
        font: { ...s.font, color: { rgb: TEAL_DARK }, underline: true },
      });
      row++;
    });
    row++; // blank
  }

  // ====== SECTION 7: ASSUMPTIONS ======
  for (let c = 0; c <= LAST_COL_STANDARD; c++) {
    setCell(ws, row, c, c === 0 ? 'ASSUMPTIONS' : '', sectionHeader);
  }
  setMerge(ws, row, 0, row, LAST_COL_STANDARD);
  row++;

  if (isBuyPath) {
    setCell(ws, row, 0, 'Down Payment', labelStyle);
    setCell(ws, row, 1, fmtCur(sym, downPayment), valRight);
    setCell(ws, row, 3, 'Interest Rate', labelStyle);
    setCell(ws, row, 4, `${interestRate}%`, valRight);
    setCell(ws, row, 6, 'Mortgage Length', labelStyle);
    setCell(ws, row, 7, `${loanTenure} years`, valRight);
    row++;
  }
  setCell(ws, row, 0, 'Mgmt Fee', labelStyle);
  setCell(ws, row, 1, `${managementFeePct}%`, valRight);
  row++;
  row++; // blank

  // Disclaimer
  const disclaimerStyle: XLSX.CellStyle = {
    font: { color: { rgb: '9CA3AF' }, sz: 8, italic: true, name: 'Calibri' },
    alignment: { horizontal: 'left', wrapText: true },
  };
  setCell(
    ws,
    row,
    0,
    'DISCLAIMER: This analysis is for informational purposes only and does not constitute financial, investment, or legal advice.',
    disclaimerStyle
  );
  setMerge(ws, row, 0, row, maxDataCol);
  row++;
  setCell(
    ws,
    row,
    0,
    'Projections are estimates based on current market data and assumptions. Actual results may vary. Consult a qualified professional before making investment decisions.',
    disclaimerStyle
  );
  setMerge(ws, row, 0, row, maxDataCol);
  row++;
  const genByStyle: XLSX.CellStyle = {
    font: { color: { rgb: TEAL_DARK }, sz: 9, bold: true, name: 'Calibri' },
    alignment: { horizontal: 'left' },
  };
  setCell(ws, row, 0, 'Generated by Airbtics (airbtics.com)', genByStyle);
  setMerge(ws, row, 0, row, Math.min(3, maxDataCol));

  // Set range
  ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: row, c: maxDataCol } });

  // Column widths (extend for rent 24-mo columns)
  const baseCols = [
    { wch: 22 },
    { wch: 28 },
    { wch: 14 },
    { wch: 16 },
    { wch: 14 },
    { wch: 24 },
    { wch: 14 },
    { wch: 30 },
  ];
  ws['!cols'] =
    maxDataCol > LAST_COL_STANDARD
      ? [
          ...baseCols,
          ...Array.from({ length: maxDataCol - LAST_COL_STANDARD }, () => ({ wch: 10 })),
        ]
      : baseCols;

  // Row heights for section headers
  ws['!rows'] = [];
  for (let r = 0; r <= row; r++) {
    ws['!rows'][r] = { hpt: r === 0 ? 24 : 16 };
  }

  // Build workbook & save
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, pathMeta.sheetName);

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });

  const safeName = addressShort.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_');
  saveAs(blob, `${safeName}_${pathMeta.fileSuffix}.xlsx`);
}
